import { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/Button/Button';
import { AccountChip } from '../account/AccountChip';
import { PrivacyNote } from '../components/PrivacyNote/PrivacyNote';
import type { BlockProgram } from '../behavior/types';
import type { Project } from '../data/types';
import { useProjects, useProjectsStoreApi } from '../stores/projectsContext';
import { ShipDialog } from './ShipDialog';
import './BehaviorView.css';

const BehaviorWorkspace = lazy(() =>
  import('./BehaviorWorkspace').then((mod) => ({ default: mod.BehaviorWorkspace })),
);
const BehaviorPreview = lazy(() =>
  import('./BehaviorPreview').then((mod) => ({ default: mod.BehaviorPreview })),
);

interface LoadState {
  loading: boolean;
  project: Project | null;
  notFound: boolean;
}

/**
 * Behavior tab — where the kid wires their trained model to behavior
 * blocks ("when class A is detected, show 🎉"). The visual block
 * editor itself is M2.5 — this skeleton owns the routing, project
 * load, and the "you need to train first" empty state, so the page
 * is reachable and testable before Blockly lands.
 */
export function BehaviorView(): JSX.Element {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const store = useProjectsStoreApi();
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({
    loading: true,
    project: null,
    notFound: false,
  });
  // The current program drives the preview's compiled behavior. We
  // seed it from the project's persisted program and update it on
  // every workspace change so the preview stays live.
  const [currentProgram, setCurrentProgram] = useState<BlockProgram | null>(null);
  const [shipDialogOpen, setShipDialogOpen] = useState(false);
  const [shipError, setShipError] = useState<string | null>(null);

  const trainedModel = useProjects((s) => s.trainedModels[projectId] ?? null);

  useEffect(() => {
    let cancelled = false;
    const found = store.getState().projects.find((p) => p.id === projectId);
    if (found) {
      setState({ loading: false, project: found, notFound: false });
      setCurrentProgram(found.blockProgram ?? null);
      void store.getState().loadTrainedModel(projectId);
      return;
    }
    void store
      .getState()
      .load()
      .then(() => {
        if (cancelled) return;
        const after = store.getState().projects.find((p) => p.id === projectId);
        setState({ loading: false, project: after ?? null, notFound: !after });
        setCurrentProgram(after?.blockProgram ?? null);
        if (after) void store.getState().loadTrainedModel(projectId);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, store]);

  if (state.loading) {
    return (
      <div className="bl-behavior bl-behavior--centered">
        <p aria-live="polite">Opening your project…</p>
      </div>
    );
  }

  if (state.notFound || !state.project) {
    return (
      <div className="bl-behavior bl-behavior--centered">
        <h1>Project not found</h1>
        <p>It might have been deleted. Head back home and pick another one.</p>
        <Button onClick={() => navigate('/studio')}>Back to studio</Button>
      </div>
    );
  }

  const { project } = state;
  const projectHref = `/studio/p/${project.id}`;
  const canShip = !!trainedModel && project.kind === 'image';

  const handleShipDownload = async (): Promise<void> => {
    setShipError(null);
    try {
      await store.getState().exportPwa(project.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not ship your app.';
      setShipError(message);
      // Re-throw so ShipDialog can flip into its error state — the
      // banner outside the dialog is a redundant safety net for the
      // kid who closes the dialog before reading it.
      throw err;
    }
  };

  return (
    <div className="bl-behavior">
      <header className="bl-behavior__header">
        <button type="button" className="bl-behavior__back" onClick={() => navigate(projectHref)}>
          ← Back to {project.name}
        </button>
        <div className="bl-behavior__header-right">
          {canShip && (
            <button
              type="button"
              className="bl-behavior__ship"
              onClick={() => setShipDialogOpen(true)}
            >
              Ship to phone
            </button>
          )}
          <PrivacyNote state="on-device" />
          <AccountChip />
        </div>
      </header>
      {shipError && (
        <p role="alert" className="bl-behavior__error">
          {shipError}
        </p>
      )}

      <section className="bl-behavior__hero">
        <p className="bl-behavior__eyebrow">Behavior</p>
        <h1 className="bl-behavior__title">Make {project.name} do something</h1>
        <p className="bl-behavior__lead">
          Wire up what your model does when it sees each class. Drag blocks together, hit Run, and
          watch your project come to life.
        </p>
      </section>

      {!trainedModel ? (
        <section className="bl-behavior__empty" aria-live="polite">
          <h2>Train your model first</h2>
          <p>
            You need a trained model before you can write behavior. Head back to your project, add
            some samples, and click Train.
          </p>
          <Button onClick={() => navigate(projectHref)}>Back to training</Button>
        </section>
      ) : (
        <div className="bl-behavior__split">
          <section className="bl-behavior__workspace" aria-label="Behavior workspace">
            <Suspense
              fallback={
                <p className="bl-behavior__placeholder" role="status">
                  Loading the block editor…
                </p>
              }
            >
              <BehaviorWorkspace
                labels={trainedModel.labels.map((l) => l.name)}
                {...(project.blockProgram ? { initialProgram: project.blockProgram } : {})}
                onSave={async (program: BlockProgram) => {
                  await store.getState().setBlockProgram(project.id, program);
                }}
                onProgramChange={setCurrentProgram}
              />
            </Suspense>
          </section>
          <Suspense
            fallback={
              <section className="bl-behavior__preview-fallback" role="status">
                Loading the preview…
              </section>
            }
          >
            <BehaviorPreview
              projectId={project.id}
              labels={trainedModel.labels.map((l) => l.name)}
              program={currentProgram}
            />
          </Suspense>
        </div>
      )}

      <ShipDialog
        isOpen={shipDialogOpen}
        projectName={project.name}
        onClose={() => setShipDialogOpen(false)}
        onDownload={handleShipDownload}
      />
    </div>
  );
}
